import { useCallback, useState } from 'react';
import { useNavigation } from '@react-navigation/native';
import { useBoolean } from 'usehooks-ts';
import { MediaTypeOptions } from 'expo-image-picker';
import { useTranslation } from 'react-i18next';
import { Nullish } from 'utility-types';

import { MAX_MEDIA_COUNT } from '@pletly/shared';
import { BlurredImages } from '@pletly/feature-auto-blur';
import { useInput } from '%hooks';
import { ImagePickerSource, usePickedImages } from '%hooks/usePickedImages';
import { PostType } from '%__generated__/types';
import { usePickedFiles } from '%hooks/usePickedFiles';
import { GetUserUploadedFilesDocument } from '%screens/files/components/__generated__/uploadedFiles.generated';
import { PostToEditData, PostTypeParams } from '../../posts/components/PostForm';
import { useSavePost } from './useSavePost';
import { PostMutationsOptions } from './mutations/postMutations';

export const useNewPost = ({
	postTypeParams,
	postToEdit,
	mutationOptions,
	isInternal,
}: {
	postTypeParams: PostTypeParams;
	postToEdit?: PostToEditData;
	mutationOptions?: PostMutationsOptions;
	isInternal?: boolean;
}) => {
	const { t } = useTranslation();
	const { navigate } = useNavigation();
	const imageSourceMenu = useImageSourceMenuState();
	const contentInput = useInput(postToEdit?.content ?? '');
	const currentMediaFiles = postToEdit?.mediaFiles || [];

	const informationFeedId =
		postTypeParams.type === PostType.Information ? postTypeParams.informationFeedId : undefined;
	const communityGroupUuid =
		postTypeParams.type === PostType.Information ? postTypeParams.communityGroupUuid : undefined;

	const screenTitle = postToEdit ? t('post.new.edit-post') : t('post.new.create-new-post');

	const { selectedImages, setSelectedImages, openImagePickerAsync, loading } = usePickedImages(
		currentMediaFiles.map((image) => ({
			type: image.type,
			uri: image.url,
			id: image.id,
			base64: '',
		})),
		{
			mediaTypes: MediaTypeOptions.All,
			allowsMultipleSelection: true,
			selectionLimit: MAX_MEDIA_COUNT,
		},
	);
	const { pickedFiles, pickFiles, onRemoveFile } = usePickedFiles(undefined, {
		maxFiles: MAX_MEDIA_COUNT - selectedImages.length,
	});

	const [blurredImages, setBlurredImages] = useState<BlurredImages>([]);

	const [scheduleDate, setScheduleDate] = useState<Date | Nullish>(
		!postToEdit?.publishedAt && postToEdit?.publishAt ? new Date(postToEdit.publishAt) : undefined,
	);

	const { savePost, saving } = useSavePost({
		id: postToEdit?.id,
		informationFeedId,
		communityGroupUuid,
		type: postTypeParams.type,
	});

	const goToPrivacy = useCallback(() => {
		navigate('NewPostPrivacy', {
			id: postToEdit?.id,
			content: contentInput.value,
			selectedUsers: postToEdit?.selectedUsers?.map(({ user }) => user.id),
			selectedMedia: selectedImages,
		});
	}, [navigate, postToEdit, selectedImages, contentInput.value]);

	const isFormValid = !!contentInput.value || selectedImages.length > 0 || pickedFiles.length > 0;
	const isInformationPost = !!informationFeedId;

	const onImageRemove = useCallback(
		(index: number) => {
			setSelectedImages((currentState) => {
				const newSelectedImages = [...currentState];
				newSelectedImages.splice(index, 1);

				return newSelectedImages;
			});
		},
		[setSelectedImages],
	);

	const onSavePress = ({
		selectedUsersIds,
		selectedTeamIds,
		selectedAllStaff,
	}: {
		selectedUsersIds?: string[];
		selectedTeamIds?: string[];
		selectedAllStaff?: boolean;
	}) => {
		const selectedMedia = [...selectedImages, ...pickedFiles];

		const refetchQueries = !pickedFiles.length
			? mutationOptions?.create?.refetchQueries
			: Array.isArray(mutationOptions?.create?.refetchQueries)
				? [...mutationOptions.create.refetchQueries, GetUserUploadedFilesDocument]
				: [GetUserUploadedFilesDocument];

		return savePost(
			{
				selectedMedia,
				content: contentInput.value.trim(),
				selectedUsers: selectedUsersIds,
				organisationTeamsIds: selectedTeamIds,
				selectedAllStaff,
				isInternal,
				publishAt: scheduleDate,
			},
			{
				...mutationOptions,
				create: {
					...mutationOptions?.create,
					refetchQueries,
				},
			},
		);
	};

	const onNewImage = useCallback(
		(source: ImagePickerSource) => () => openImagePickerAsync(source),
		[openImagePickerAsync],
	);

	const isCommunityPost =
		postTypeParams.type === PostType.Information && !!postTypeParams.communityGroupUuid;
	const isPostOnUserFeed =
		postTypeParams.type === PostType.Feed && !!postTypeParams?.selectedUsers?.length;

	return {
		screenTitle,
		imageSourceMenu,
		selectedImages,
		setSelectedImages,
		blurredImages,
		setBlurredImages,
		pickedFiles,
		pickFiles,
		onRemoveFile,
		contentInput,
		isFormValid,
		isInformationPost,
		isCommunityPost,
		isPostOnUserFeed,
		goToPrivacy,
		saving,
		loading,
		onSavePress,
		onNewImage,
		onImageRemove,
		scheduleDate,
		setScheduleDate,
	};
};

const useImageSourceMenuState = () => {
	const { setTrue: open, setFalse: close, value: visible } = useBoolean();

	return {
		open,
		close,
		visible,
	};
};
